<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { SearchMode } from "~/core/composables/useSearch";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import { Filters } from "~/core/models/filters";
import { formatIsoDate } from "~/core/utils/date";
import { useDtwinOperations } from "~/modules/dtwin/composables/useDtwinOperationManager";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";
import type { DtwinOperationFirmware } from "~/modules/dtwin/models/DtwinOperationFirmware.interface";
import type { DtwinOperationFirmwareInputParameters } from "~/modules/dtwin/models/DtwinOperationFirmwareInputParameters.interface";
import { DtwinOperationTypes } from "~/modules/dtwin/models/DtwinOperationTypes.enum";
import { OperationServiceOrigin } from "~/modules/dtwin/models/OperationServiceOrigin.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";

const props = defineProps<{
  organizationCode: number;
  dtwin: Dtwin;
  dtwinModel: DtwinModel;
  dtwinOperationModel: OperationModel;
  reload: boolean;
}>();

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const { error, fetchData, isLoading, onSearch, results, search, setFilter } =
  useDtwinOperations({
    searchMode: SearchMode.FILTER_SEARCH,
    useQueryParametersForPagination: false,
    defaultSort: "-launchPlannedAt",
    organizationCode: currentOrganization.value?.code,
  });

const resultsFirmware = results as unknown as Ref<
  PaginatedList<DtwinOperationFirmware>
>;

const operationFormShown = ref(false);
const operationFormModel: Ref<
  DtwinOperation<DtwinOperationFirmwareInputParameters> | undefined
> = ref();

/**
 * Retrieve operations
 * @param page
 * @param pageSize
 */
async function loadData(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  await fetchData(page, pageSize);
}

function initializeDefaultFilters() {
  setFilter(Filters.DTWIN_OPERATION_UID, props.dtwin?.uid ?? "");
  setFilter(Filters.DTWIN_OPERATION_TYPE, DtwinOperationTypes.FIRMWARE_UPDATE);
  setFilter(
    Filters.DTWIN_OPERATION_SERVICE_ORIGIN,
    OperationServiceOrigin.UNITARY,
  );
}

const reloadWithContext = () => {
  loadData(
    results.value?.pagination?.page,
    results.value?.pagination?.pageSize,
  );
};

initializeDefaultFilters();

const onDeviceChange = async (dtwin?: Dtwin | null) => {
  setFilter(Filters.DTWIN_OPERATION_UID, dtwin?.uid ?? "");
  if (dtwin) {
    await search();
  }
};

function showOperationForm(
  operation: DtwinOperation<DtwinOperationFirmwareInputParameters>,
) {
  operationFormModel.value = operation;
  operationFormShown.value = true;
}

onSearch(loadData);

const dateFormat = t("operation.dateFormat");

const translatedError: ComputedRef<Error | null | undefined> = computed(() =>
  error.value?.toError(),
);

watch(() => props.dtwin, onDeviceChange);
watch(() => props.reload, reloadWithContext);
</script>

<template>
  <lxc-table
    min-width="84rem"
    :is-loading="isLoading"
    :error="translatedError"
    :data="resultsFirmware?.results"
    :empty-text="t('operation.empty')"
    :context="resultsFirmware?.pagination"
    data-cy="product-details-operations-table"
    @change-page-and-page-size="loadData"
  >
    <lxc-table-column
      prop="customInputParameters.targetVersion"
      :label="t('operation.firmware.update.version')"
      min-width="12rem"
    />

    <lxc-table-column
      prop="createdAt"
      :label="t('operation.createdAt')"
      width="15rem"
    >
      <template #default="scope">
        {{ formatIsoDate(scope.row.createdAt, dateFormat) }}
      </template>
    </lxc-table-column>

    <lxc-table-column
      prop="launchPlannedAt"
      :label="t('operation.launchedAt')"
      width="15rem"
    >
      <template #default="scope">
        {{ formatIsoDate(scope.row.launchPlannedAt, dateFormat) }}
      </template>
    </lxc-table-column>

    <lxc-table-column
      prop="modifiedAt"
      :label="t('operation.updatedAt')"
      width="15rem"
    >
      <template #default="scope">
        {{
          scope.row.updatedAt
            ? formatIsoDate(scope.row.updatedAt, dateFormat)
            : ""
        }}
      </template>
    </lxc-table-column>

    <lxc-table-column prop="status" :label="t('operation.state')" width="15rem">
      <template #default="scope">
        <operation-mgr-operation-status-badge :status="scope.row.status" />
      </template>
    </lxc-table-column>

    <lxc-table-column v-if="dtwin" prop="actions" class="!py-0" width="12rem">
      <template #default="scope">
        <dtwin-form-operation-list-actions
          :organization-code="organizationCode"
          :dtwin="dtwin"
          :dtwin-model="dtwinModel"
          :dtwin-operation-model="dtwinOperationModel"
          :operation="scope.row"
          @change="reloadWithContext"
          @edit="
            (
              operation: DtwinOperation<DtwinOperationFirmwareInputParameters>,
            ) => showOperationForm(operation)
          "
        />
      </template>
    </lxc-table-column>
  </lxc-table>

  <dtwin-form-firmware-update-modal
    v-model:show="operationFormShown"
    :organization-code="organizationCode"
    :dtwin="dtwin"
    :dtwin-model="dtwinModel"
    :dtwin-operation-model="dtwinOperationModel"
    :operation="operationFormModel"
    @change="reloadWithContext"
  />
</template>

<style lang="scss" scoped>
:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;
        }
      }
    }
  }
}
</style>
